import { Button } from "../ui/button";
import { ArrowUp, ArrowDown, Edit2, Trash2 } from "lucide-react";
import type { CauseItem } from "./useCausesManager";

interface CauseRowActionsProps {
  cause: CauseItem;
  idx: number;
  totalCount: number;
  onEdit: (cause: CauseItem) => void;
  onDelete: (id: string) => void;
  onMove: (index: number, direction: "up" | "down") => void;
}

function CauseRowActions({
  cause,
  idx,
  totalCount,
  onEdit,
  onDelete,
  onMove,
}: CauseRowActionsProps) {
  return (
    <>
      <td className="p-4 text-center">
        <div className="flex items-center justify-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onMove(idx, "up")}
            disabled={idx === 0}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ArrowUp className="h-4.5 w-4.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onMove(idx, "down")}
            disabled={idx === totalCount - 1}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ArrowDown className="h-4.5 w-4.5" />
          </Button>
        </div>
      </td>
      <td className="p-4 text-center">
        <div className="flex items-center justify-center gap-2">
          <Button
            size="icon"
            variant="outline"
            onClick={() => onEdit(cause)}
            className="h-8 w-8 border-border/60 hover:bg-brand-primary/10 hover:text-brand-primary text-muted-foreground"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            onClick={() => onDelete(cause.id)}
            className="h-8 w-8 border-border/60 hover:bg-destructive/10 hover:text-destructive text-muted-foreground"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </>
  );
}

interface CauseRowProps {
  cause: CauseItem;
  idx: number;
  totalCount: number;
  onEdit: (cause: CauseItem) => void;
  onDelete: (id: string) => void;
  onMove: (index: number, direction: "up" | "down") => void;
}

function CauseRow({
  cause,
  idx,
  totalCount,
  onEdit,
  onDelete,
  onMove,
}: CauseRowProps) {
  return (
    <tr className="hover:bg-muted/10 transition-colors">
      <td className="p-4 text-center font-mono font-bold text-muted-foreground">
        {idx + 1}
      </td>
      <td className="p-4 w-16">
        <div className="w-10 h-10 rounded-full bg-muted/30 border border-border/60 flex items-center justify-center text-lg select-none text-muted-foreground">
          {cause.icon}
        </div>
      </td>
      <td className="p-4 max-w-xs space-y-1">
        <p className="font-semibold text-foreground leading-snug">
          {cause.title_en}
        </p>
        {cause.title_tr && cause.title_tr !== cause.title_en && (
          <p className="text-xs text-muted-foreground italic">
            {cause.title_tr}
          </p>
        )}
      </td>
      <td className="p-4 max-w-md space-y-1">
        <p className="text-xs text-foreground/85 line-clamp-2 leading-relaxed">
          {cause.desc_en}
        </p>
        {cause.desc_tr && cause.desc_tr !== cause.desc_en && (
          <p className="text-[11px] text-muted-foreground/80 italic line-clamp-2 leading-relaxed">
            {cause.desc_tr}
          </p>
        )}
      </td>
      <CauseRowActions
        cause={cause}
        idx={idx}
        totalCount={totalCount}
        onEdit={onEdit}
        onDelete={onDelete}
        onMove={onMove}
      />
    </tr>
  );
}

interface CausesListProps {
  causes: CauseItem[];
  onMove: (index: number, direction: "up" | "down") => void;
  onEdit: (cause: CauseItem) => void;
  onDelete: (id: string) => void;
}

export function CausesList({ causes, onMove, onEdit, onDelete }: CausesListProps) {
  if (causes.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-border/60 rounded-2xl bg-card/40">
        <p className="text-sm font-semibold text-muted-foreground">
          No value cards configured. Click the button above to add one.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border/60 bg-card rounded-2xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse font-sans">
          <thead>
            <tr className="bg-muted/40 border-b border-border/60 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <th className="p-4 w-12 text-center">#</th>
              <th className="p-4 w-16">Icon</th>
              <th className="p-4">Title (EN / TR)</th>
              <th className="p-4">Description (EN / TR)</th>
              <th className="p-4 w-28 text-center">Order</th>
              <th className="p-4 w-36 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40 text-sm">
            {causes.map((cause, idx) => (
              <CauseRow
                key={cause.id}
                cause={cause}
                idx={idx}
                totalCount={causes.length}
                onEdit={onEdit}
                onDelete={onDelete}
                onMove={onMove}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CausesList;

